import React, { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Upload, X, Loader2 } from 'lucide-react';
import ImageCropperModal from './ImageCropperModal';

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

/**
 * Reusable image upload widget. Accepts a value (URL or data-URL) and calls
 * onChange with the new URL. Falls back to a plain URL text input for cases
 * where the user has a hosted URL.
 */
export default function ImageUpload({ value, onChange, label = 'Image', testId = 'image-upload', aspect = 4 / 5 }) {
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const inputRef = useRef(null);

  const pickFile = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Max 8 MB before crop'); return; }
    try {
      const dataUrl = await fileToDataUrl(file);
      setCropSrc(dataUrl);
    } catch {
      toast.error('Could not read file');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const uploadCropped = async (dataUrl) => {
    setCropSrc(null);
    setUploading(true);
    try {
      // Convert cropped data-url to blob for upload
      const blob = await (await fetch(dataUrl)).blob();
      const form = new FormData();
      form.append('file', blob, 'upload.jpg');
      const { data } = await api.post('/admin/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data.data_url);
      toast.success('Uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const clear = () => onChange('');

  return (
    <div data-testid={testId}>
      {label && <label className="block text-xs opacity-80 mb-1" style={{ color: 'var(--heritage-plum)' }}>{label}</label>}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden flex items-center justify-center"
          style={{ background: 'rgba(75,24,56,0.05)', border: '1.5px dashed rgba(75,24,56,0.3)' }}>
          {value ? (
            <img src={value} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <Upload size={22} style={{ color: 'var(--heritage-plum)', opacity: 0.5 }} />
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={pickFile} disabled={uploading}
              className="h-btn-outline text-xs" data-testid={`${testId}-choose`}>
              {uploading ? <><Loader2 size={12} className="mr-1 animate-spin" /> Uploading...</> : <><Upload size={12} className="mr-1" /> {value ? 'Replace' : 'Upload image'}</>}
            </button>
            {value && (
              <button type="button" onClick={clear} className="h-btn-outline text-xs"
                style={{ borderColor: '#9A2C2C', color: '#9A2C2C' }} data-testid={`${testId}-clear`}>
                <X size={12} className="mr-1" /> Remove
              </button>
            )}
            <button type="button" onClick={() => setShowUrl(!showUrl)} className="h-btn-outline text-xs" data-testid={`${testId}-toggle-url`}>
              {showUrl ? 'Hide URL' : 'Or paste URL'}
            </button>
          </div>
          {showUrl && (
            <input type="url" value={value || ''} onChange={(e) => onChange(e.target.value)}
              placeholder="https://..." className="w-full rounded-md px-2 py-1.5 text-xs"
              style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)' }}
              data-testid={`${testId}-url`} />
          )}
          <div className="text-[10px] opacity-70" style={{ color: 'var(--heritage-plum)' }}>PNG, JPG, WebP. Max 8 MB before crop. You'll crop each image before it saves.</div>
        </div>
        <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      </div>

      {cropSrc && (
        <ImageCropperModal
          src={cropSrc}
          defaultRatio={aspect}
          onCancel={() => setCropSrc(null)}
          onConfirm={uploadCropped}
        />
      )}
    </div>
  );
}
